// #region imports

// --- Angular
import { Injectable } from '@angular/core';

// --- RxJs
import { BehaviorSubject, Observable } from 'rxjs';

// --- HS Code
import { SurveyResultsModel, ResultsViews, ScoreModels, ScoreModel } from '../../../models';

// #endregion

@Injectable({
  providedIn: 'root'
})
export abstract class ResultsBaseService {
  protected _surveyResults$: BehaviorSubject<SurveyResultsModel>;
  get surveyResults$(): Observable<SurveyResultsModel> {
    return this._surveyResults$;
  }

  protected _widgetDisplayMode$: BehaviorSubject<ResultsViews>;
  get widgetDisplayMode$(): Observable<ResultsViews> {
    return this._widgetDisplayMode$;
  }

  protected _scoreList$: BehaviorSubject<ScoreModels>;
  get scoreList$(): Observable<ScoreModels> {
    return this._scoreList$;
  }

  constructor() {
    this._surveyResults$ = new BehaviorSubject<SurveyResultsModel>(null);
    this._widgetDisplayMode$ = new BehaviorSubject<ResultsViews>(ResultsViews.chart);
    this._scoreList$ = new BehaviorSubject<ScoreModels>(null);
  }

  abstract loadSurveyResults(identifier: string, isFullResults: boolean): void;

  setWidgetDisplayMode(mode: ResultsViews): void {
    this._widgetDisplayMode$.next(mode);
  }

  protected _mapScores(results: SurveyResultsModel): void {
    if (!results) {
      return;
    }

    // sort results
    if (results.benchmark
      && results.benchmark.fear
      && results.benchmark.humility
      && results.benchmark.love
      && results.benchmark.pride
    ) {
      results.benchmark.fear.indicators.sort((a, b) => a.index - b.index);
      results.benchmark.humility.indicators.sort((a, b) => a.index - b.index);
      results.benchmark.love.indicators.sort((a, b) => a.index - b.index);
      results.benchmark.pride.indicators.sort((a, b) => a.index - b.index);
    }

    if (results.self
      && results.self.fear
      && results.self.humility
      && results.self.love
      && results.self.pride
    ) {
      results.self.fear.indicators.sort((a, b) => a.index - b.index);
      results.self.humility.indicators.sort((a, b) => a.index - b.index);
      results.self.love.indicators.sort((a, b) => a.index - b.index);
      results.self.pride.indicators.sort((a, b) => a.index - b.index);
    }

    if (results.others
        && results.others.fear
        && results.others.humility
        && results.others.love
        && results.others.pride
      ) {
      results.others.fear.indicators.sort((a, b) => a.index - b.index);
      results.others.humility.indicators.sort((a, b) => a.index - b.index);
      results.others.love.indicators.sort((a, b) => a.index - b.index);
      results.others.pride.indicators.sort((a, b) => a.index - b.index);
    }


    const models = {} as ScoreModels;
    models.benchmark = new Array<ScoreModel[]>();
    models.self = new Array<ScoreModel[]>();
    models.others = new Array<ScoreModel[]>();
    models.effectiveness = new Array<number>();
    models.comments  = new Array<string>();

    for (let i = 0; i < 4; i++) {
      models.benchmark.push(new Array<ScoreModel>());
      models.benchmark[i].push({
        categoryName: results.benchmark.humility.indicators[i].categoryName,
        quadrantName: 'humility',
        score: results.benchmark.humility.indicators[i].score,
        index: results.benchmark.humility.indicators[i].index
      } as ScoreModel);
      models.benchmark[i].push({
        categoryName: results.benchmark.love.indicators[i].categoryName,
        quadrantName: 'love',
        score: results.benchmark.love.indicators[i].score,
        index: results.benchmark.love.indicators[i].index
      } as ScoreModel);
    }

    for (let i = 0; i < 4; i++) {
      models.benchmark.push(new Array<ScoreModel>());
      models.benchmark[i + 4].push({
        categoryName: results.benchmark.pride.indicators[i].categoryName,
        quadrantName: 'pride',
        score: results.benchmark.pride.indicators[i].score,
        index: results.benchmark.pride.indicators[i].index
      } as ScoreModel);
      models.benchmark[i + 4].push({
        categoryName: results.benchmark.fear.indicators[i].categoryName,
        quadrantName: 'fear',
        score: results.benchmark.fear.indicators[i].score,
        index: results.benchmark.fear.indicators[i].index
      } as ScoreModel);
    }

    for (let i = 0; i < 4; i++) {
      models.self.push(new Array<ScoreModel>());
      models.self[i].push({
        categoryName: results.self.humility.indicators[i].categoryName,
        quadrantName: 'humility',
        score: results.self.humility.indicators[i].score,
        index: results.self.humility.indicators[i].index
      } as ScoreModel);
      models.self[i].push({
        categoryName: results.self.love.indicators[i].categoryName,
        quadrantName: 'love',
        score: results.self.love.indicators[i].score,
        index: results.self.love.indicators[i].index
      } as ScoreModel);
    }

    for (let i = 0; i < 4; i++) {
      models.self.push(new Array<ScoreModel>());
      models.self[i + 4].push({
        categoryName: results.self.pride.indicators[i].categoryName,
        quadrantName: 'pride',
        score: results.self.pride.indicators[i].score,
        index: results.self.pride.indicators[i].index
      } as ScoreModel);
      models.self[i + 4].push({
        categoryName: results.self.fear.indicators[i].categoryName,
        quadrantName: 'fear',
        score: results.self.fear.indicators[i].score,
        index: results.self.fear.indicators[i].index
      } as ScoreModel);
    }

    if (results.effectivenessModel){

      for (let i = 0; i < 8; i++) {
        models.effectiveness.push(results.effectivenessModel.effectiveness[i].selfScore);
      }

    }

    if (results.commentsQuestionPages){

      for (let i = 0; i < 3; i++) {
        models.comments.push(results.commentsQuestionPages[0].sections[0].commentsQuestions[i].questionResponses[0]); 
      }

    }

    this._scoreList$.next(models);
  }
}
